import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { PublicReference } from '../../../shared/types/publicDirectory';

interface PublicGalleryProps {
  references: PublicReference[];
  viewMode: 'grid' | 'masonry' | 'list';
  isLoading?: boolean;
}

export function PublicGallery({ references, viewMode, isLoading }: PublicGalleryProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (references.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-base-content/50 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-base-content/70 mb-2">No references found</h3>
        <p className="text-base-content/50">Try adjusting your filters or search terms.</p>
      </div>
    );
  }

  const handleReferenceClick = (reference: PublicReference) => {
    // Navigate to public reference detail page
    navigate(`/public/reference/${reference._id}`);
  };

  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {references.map((reference) => (
          <div
            key={reference._id}
            className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => handleReferenceClick(reference)}
          >
            <div className="card-body">
              <div className="flex gap-4">
                {/* Thumbnail */}
                {reference.imageUrl && (
                  <div className="flex-shrink-0">
                    <img
                      src={reference.imageUrl}
                      alt={reference.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="card-title text-lg mb-2">{reference.title}</h3>
                  {reference.description && (
                    <p className="text-base-content/70 text-sm line-clamp-2 mb-2">
                      {reference.description}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 text-xs">
                    <div className="badge badge-secondary">{reference.type}</div>
                    {reference.category && (
                      <div className="badge badge-outline">{reference.category}</div>
                    )}
                    {reference.author?.displayName && (
                      <span className="text-base-content/50">
                        by {reference.author.displayName}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-2 text-xs text-base-content/50">
                    <span>👁 {reference.publicMetadata?.views || 0} views</span>
                    <span>❤️ {reference.publicMetadata?.likes || 0} likes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (viewMode === 'masonry') {
    return (
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
        {references.map((reference) => (
          <div
            key={reference._id}
            className="break-inside-avoid mb-4 cursor-pointer"
            onClick={() => handleReferenceClick(reference)}
          >
            <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow">
              {reference.imageUrl && (
                <figure>
                  <img
                    src={reference.imageUrl}
                    alt={reference.title}
                    className="w-full h-auto object-cover"
                  />
                </figure>
              )}
              <div className="card-body p-4">
                <h3 className="card-title text-sm">{reference.title}</h3>
                {reference.description && (
                  <p className="text-xs text-base-content/70 line-clamp-3">
                    {reference.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between mt-2">
                  <div className="badge badge-secondary badge-sm">{reference.type}</div>
                  <div className="flex gap-2 text-xs text-base-content/50">
                    <span>👁 {reference.publicMetadata?.views || 0}</span>
                    <span>❤️ {reference.publicMetadata?.likes || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Grid view (default)
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {references.map((reference) => (
        <div
          key={reference._id}
          className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => handleReferenceClick(reference)}
        >
          {reference.imageUrl && (
            <figure className="px-4 pt-4">
              <img
                src={reference.imageUrl}
                alt={reference.title}
                className="rounded-xl max-h-48 w-full object-cover"
              />
            </figure>
          )}
          <div className="card-body p-4">
            <h3 className="card-title text-sm">{reference.title}</h3>
            {reference.description && (
              <p className="text-xs text-base-content/70 line-clamp-2">
                {reference.description}
              </p>
            )}
            
            <div className="flex items-center gap-2 mt-2">
              <div className="badge badge-secondary badge-sm">{reference.type}</div>
              {reference.category && (
                <div className="badge badge-outline badge-sm">{reference.category}</div>
              )}
            </div>
            
            <div className="flex items-center justify-between mt-2">
              {reference.author?.displayName && (
                <span className="text-xs text-base-content/50">
                  by {reference.author.displayName}
                </span>
              )}
              <div className="flex gap-2 text-xs text-base-content/50">
                <span>👁 {reference.publicMetadata?.views || 0}</span>
                <span>❤️ {reference.publicMetadata?.likes || 0}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}